import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useAdminCustomerMenuRestriction } from "../../../hook/useAdminCustomerMenuRestriction";
import { canManageDoctorSchedule } from "./doctorScheduleAccess";
import "./stylesSchedule.css";
import "./doctorSchedule.css";

const localDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const rowsFrom = (snapshot) => snapshot.docs.map((item) => ({ ...item.data(), id: item.id }));
const visitTime = (visit) => visit.startTime && visit.endTime ? `${visit.startTime}–${visit.endTime}` : "Time not set";
const sortNames = (rows) => rows.sort((a, b) => a.name.localeCompare(b.name, "th"));

// Keep the restricted page unmounted until the existing menu scope allows it.
export default function DoctorSchedule() {
  const restricted = useAdminCustomerMenuRestriction();
  return restricted ? <div className="alert alert-info" role="status">The doctor schedule is available after your account's full customer access is verified.</div> : <DoctorSchedulePage />;
}

export function DoctorSchedulePage() {
  const { currentUser } = useAuth();
  const canEdit = canManageDoctorSchedule(currentUser.roles);
  const [month, setMonth] = useState(() => localDate(new Date()).slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(() => localDate(new Date()));
  const [doctors, setDoctors] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [error, setError] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState("");
  const [nodeFilter, setNodeFilter] = useState("");
  const [form, setForm] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [monthlyDoctor, setMonthlyDoctor] = useState(null);
  const [savedMessage, setSavedMessage] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5)) - 1;
  const dateFor = (day) => `${month}-${String(day).padStart(2, "0")}`;
  const days = Array.from({ length: new Date(year, monthIndex + 1, 0).getDate() }, (_, i) => i + 1);

  useEffect(() => {
    let cancelled = false;
    setLookupLoading(true);
    setLookupError("");
    Promise.all([getDocs(collection(db, "doctors")), getDocs(collection(db, "nodes"))])
      .then(([doctorDocs, nodeDocs]) => {
        if (cancelled) return;
        setDoctors(sortNames(rowsFrom(doctorDocs).map((item) => ({ ...item, name: item.name || item.id }))));
        setNodes(sortNames(rowsFrom(nodeDocs).map((item) => ({ ...item, name: item.nodename || item.id }))));
      }).catch(() => { if (!cancelled) setLookupError("Unable to load doctors and nodes. Please retry."); })
      .finally(() => { if (!cancelled) setLookupLoading(false); });
    return () => { cancelled = true; };
  }, [retry]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setVisits([]);
    const nextMonth = localDate(new Date(year, monthIndex + 1, 1));
    const unsubscribe = onSnapshot(query(collection(db, "doctorSchedules"),
      where("date", ">=", `${month}-01`), where("date", "<", nextMonth)), (snapshot) => {
      if (!active) return;
      setVisits(rowsFrom(snapshot));
      setLoading(false);
      setError("");
    }, () => {
      if (!active) return;
      setVisits([]);
      setError("Unable to load the OPD schedule. Please retry.");
      setLoading(false);
    });
    return () => { active = false; unsubscribe(); };
  }, [month, year, monthIndex, retry]);

  const formNodeId = form ? form.nodeId : "";
  useEffect(() => {
    let cancelled = false;
    setCustomers([]);
    setCustomerError("");
    if (!formNodeId) { setCustomerLoading(false); return undefined; }
    setCustomerLoading(true);
    getDocs(collection(db, "nodes", formNodeId, "customers")).then((snapshot) => {
      if (!cancelled) setCustomers(sortNames(rowsFrom(snapshot).map((item) => ({
        ...item, name: item.CustomerNameEN || item.CustomerNameTH || item.id,
      }))));
    }).catch(() => { if (!cancelled) setCustomerError("Unable to load customers. Close and reopen this visit to retry."); })
      .finally(() => { if (!cancelled) setCustomerLoading(false); });
    return () => { cancelled = true; };
  }, [formNodeId]);

  const changeMonth = (value) => {
    if (!/^\d{4}-\d{2}$/.test(value) || Number(value.slice(5)) < 1 || Number(value.slice(5)) > 12 || Number(value.slice(0, 4)) < 1000) return;
    setMonth(value);
    setSelectedDate(`${value}-01`);
  };
  const moveMonth = (offset) => changeMonth(localDate(new Date(year, monthIndex + offset, 1)).slice(0, 7));
  const openVisit = (visit = {}) => {
    setFormError("");
    setConfirmDelete(false);
    setSavedMessage("");
    const nextForm = { doctorId: "", date: selectedDate || `${month}-01`, nodeId: "", customerId: "", startTime: "", endTime: "", ...visit };
    setForm(nextForm);
    setOriginalForm(nextForm);
  };
  const canLeaveDraft = () => !monthlyDoctor || JSON.stringify(form) === JSON.stringify(originalForm) || window.confirm("Discard unsaved changes to this OPD visit?");
  const closeForm = () => {
    if (!busy && canLeaveDraft()) { setForm(null); setMonthlyDoctor(null); }
  };
  const chooseMonthlyVisit = (visit) => { if (!busy && canLeaveDraft()) openVisit(visit); };
  const openDoctorMonth = (doctor) => {
    setMonthlyDoctor(doctor);
    const date = selectedDate || `${month}-01`;
    const existing = visits.filter((visit) => visit.doctorId === doctor.id && visit.date === date)
      .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""))[0];
    openVisit(existing || { doctorId: doctor.id, date });
  };

  const saveVisit = async (event) => {
    event.preventDefault();
    if (!canEdit || busy) return;
    const doctor = doctors.find((item) => item.id === form.doctorId);
    const node = nodes.find((item) => item.id === form.nodeId);
    const customer = customers.find((item) => item.id === form.customerId);
    if (!doctor || !node || !customer || customerLoading || customerError || !/^\d{4}-\d{2}-\d{2}$/.test(form.date) || localDate(new Date(`${form.date}T12:00:00`)) !== form.date) {
      setFormError("Select a doctor, a valid OPD date, a node, and a customer belonging to that node.");
      return;
    }
    const validTime = /^([01]\d|2[0-3]):[0-5]\d$/;
    if ((!form.id || form.startTime || form.endTime) &&
        (!validTime.test(form.startTime) || !validTime.test(form.endTime) || form.startTime >= form.endTime)) {
      setFormError("Enter a start and end time. End time must be later than start time on the same day.");
      return;
    }
    setBusy(true);
    setFormError("");
    const data = { doctorId: doctor.id, doctorName: doctor.name, date: form.date,
      nodeId: node.id, nodeName: node.name, customerId: customer.id, customerName: customer.name, startTime: form.startTime, endTime: form.endTime };
    try {
      let id = form.id;
      if (id) await updateDoc(doc(db, "doctorSchedules", id), data);
      else id = (await addDoc(collection(db, "doctorSchedules"), data)).id;
      const savedVisit = { ...data, id };
      setVisits((current) => [...current.filter((visit) => visit.id !== id), savedVisit]);
      setMonth(form.date.slice(0, 7));
      setSelectedDate(form.date);
      if (monthlyDoctor) {
        setForm(savedVisit);
        setOriginalForm(savedVisit);
        setSavedMessage(`Saved OPD visit on ${form.date}. Select another day to continue.`);
      } else setForm(null);
    } catch (err) { setFormError("Unable to save the OPD visit. Your changes are still here; please try again."); }
    finally { setBusy(false); }
  };
  const removeVisit = async () => {
    if (!canEdit || busy || !form.id) return;
    setBusy(true);
    setFormError("");
    try {
      await deleteDoc(doc(db, "doctorSchedules", form.id));
      setVisits((current) => current.filter((visit) => visit.id !== form.id));
      if (monthlyDoctor) {
        openVisit({ doctorId: monthlyDoctor.id, date: form.date });
        setSavedMessage("OPD visit deleted.");
      } else setForm(null);
    }
    catch (err) { setFormError("Unable to delete the OPD visit. Please try again."); }
    finally { setBusy(false); }
  };

  const needle = search.trim().toLocaleLowerCase();
  const filteredVisits = visits.filter((visit) => (!nodeFilter || visit.nodeId === nodeFilter) &&
    `${visit.doctorName} ${visit.nodeName} ${visit.customerName}`.toLocaleLowerCase().includes(needle));
  const doctorMap = new Map(doctors.map((doctor) => [doctor.id, doctor]));
  visits.forEach((visit) => { if (!doctorMap.has(visit.doctorId)) doctorMap.set(visit.doctorId, { id: visit.doctorId, name: visit.doctorName }); });
  const visibleDoctors = sortNames(Array.from(doctorMap.values())).filter((doctor) =>
    (!nodeFilter && (!needle || doctor.name.toLocaleLowerCase().includes(needle))) || filteredVisits.some((visit) => visit.doctorId === doctor.id));
  const details = filteredVisits.filter((visit) => !selectedDate || visit.date === selectedDate)
    .sort((a, b) => a.date.localeCompare(b.date) || a.doctorName.localeCompare(b.doctorName, "th") || (a.startTime || "").localeCompare(b.startTime || ""));
  const doctorMonthVisits = monthlyDoctor ? visits.filter((visit) => visit.doctorId === monthlyDoctor.id)
    .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")) : [];
  const dayVisits = form ? doctorMonthVisits.filter((visit) => visit.date === form.date) : [];
  const ready = !loading && !lookupLoading && !error && !lookupError;

  return <div className="card"><div className="card-body tech-schedule-container doctor-schedule">
    <div className="tech-schedule-toolbar">
      <div><h3 className="mb-1">Doctor Schedule</h3><span className="tech-schedule-subtitle">OPD dates and locations · Node → Customer</span></div>
      {canEdit && <button className="btn btn-primary btn-sm" disabled={!ready} onClick={() => openVisit()}>Add OPD visit</button>}
    </div>
    <div className="tech-month-navigation">
      <button className="btn btn-outline-primary btn-sm" aria-label="Previous month" onClick={() => moveMonth(-1)}>‹</button>
      <label className="mb-0">Month <input className="form-control" aria-label="Schedule month" type="month" min="1000-01" value={month} onChange={(event) => changeMonth(event.target.value)} /></label>
      <button className="btn btn-outline-primary btn-sm" aria-label="Next month" onClick={() => moveMonth(1)}>›</button>
      <button className="btn btn-outline-secondary btn-sm" onClick={() => { const today = localDate(new Date()); setMonth(today.slice(0, 7)); setSelectedDate(today); }}>Today</button>
    </div>
    <div className="tech-schedule-toolbar tech-filter-bar"><div className="tech-schedule-filters">
      <input className="form-control" type="search" aria-label="Search doctor or location" placeholder="Doctor / node / customer" value={search} onChange={(event) => setSearch(event.target.value)} />
      <select className="form-control" aria-label="Filter node" value={nodeFilter} onChange={(event) => setNodeFilter(event.target.value)}><option value="">All nodes</option>{nodes.map((node) => <option key={node.id} value={node.id}>{node.name}</option>)}</select>
      <button className="btn btn-link btn-sm" onClick={() => { setSearch(""); setNodeFilter(""); setSelectedDate(""); }}>Reset filters</button>
    </div></div>
    {(error || lookupError) && <div className="alert alert-danger" role="alert">{error || lookupError} <button className="btn btn-link btn-sm" onClick={() => setRetry(retry + 1)}>Retry</button></div>}
    {(loading || lookupLoading) && <p role="status">Loading OPD schedule…</p>}
    {ready && <React.Fragment>
      <div className="tech-schedule-scroll" role="region" aria-label="Doctor OPD monthly schedule" tabIndex={0}>
        <table className="tech-schedule-table doctor-month-table"><thead><tr>
          <th className="tech-name-heading" scope="col">Doctor</th>
          {days.map((day) => <th key={day} scope="col" className={selectedDate === dateFor(day) ? "doctor-selected-date" : ""}>
            <button className="tech-date-button" aria-label={`Show OPD visits on ${dateFor(day)}`} onClick={() => setSelectedDate(dateFor(day))}>{day}<small className="d-block">{new Date(year, monthIndex, day).toLocaleDateString("en-GB", { weekday: "short" })}</small></button>
          </th>)}
        </tr></thead><tbody>{visibleDoctors.map((doctor) => <tr key={doctor.id}>
          <th className="tech-name-cell" scope="row">{canEdit && doctors.some((item) => item.id === doctor.id) ? <button className="tech-name-button" title="Edit monthly OPD visits" onClick={() => openDoctorMonth(doctor)}>{doctor.name}</button> : doctor.name}</th>
          {days.map((day) => {
            const appointments = filteredVisits.filter((visit) => visit.doctorId === doctor.id && visit.date === dateFor(day));
            return <td key={day} className={appointments.length ? "tech-night-cell" : ""}>
              <button className="tech-month-cell" aria-label={`${doctor.name}, ${dateFor(day)}, ${appointments.length} OPD visits`} title={appointments.map((visit) => `${visitTime(visit)} · ${visit.nodeName} → ${visit.customerName}`).join("\n")}
                onClick={() => { setSelectedDate(dateFor(day)); }}>{appointments.length ? <React.Fragment><strong>OPD</strong><small className="d-block">{appointments.length}</small></React.Fragment> : "–"}</button>
            </td>;
          })}
        </tr>)}{!visibleDoctors.length && <tr><td colSpan={days.length + 1}>No doctors match the current filters.</td></tr>}</tbody></table>
      </div>
      <section className="tech-day-details" aria-label="OPD visit details"><div className="tech-schedule-toolbar">
        <h4 className="mb-0">{selectedDate ? `OPD · ${selectedDate}` : `OPD · ${month}`}</h4>
        <button className="btn btn-outline-primary btn-sm" onClick={() => setSelectedDate("")}>Show whole month</button>
      </div><div className="table-responsive"><table className="table table-sm"><thead><tr><th>OPD date</th><th>Time</th><th>Doctor</th><th>Node → Customer</th>{canEdit && <th>Action</th>}</tr></thead>
        <tbody>{details.map((visit) => <tr key={visit.id}><td className="text-nowrap">{visit.date}</td><td className="text-nowrap">{visitTime(visit)}</td><td>{visit.doctorName}</td><td>{visit.nodeName} → {visit.customerName}</td>{canEdit && <td><button className="btn btn-outline-primary btn-sm" onClick={() => openVisit(visit)}>Edit</button></td>}</tr>)}
          {!details.length && <tr><td colSpan={canEdit ? 5 : 4}>No OPD visits for the selected date and filters.</td></tr>}
        </tbody></table></div></section>
    </React.Fragment>}
    <Modal className="doctor-visit-modal" scrollable size={monthlyDoctor ? "xl" : undefined} show={Boolean(form)} onHide={closeForm} centered backdrop={busy ? "static" : true} keyboard={!busy}>
      <Modal.Header closeButton={!busy}><Modal.Title>{monthlyDoctor ? `${monthlyDoctor.name} · ${month} OPD visits` : form && form.id ? "Edit OPD visit" : "Add OPD visit"}</Modal.Title></Modal.Header>
      {form && <form onSubmit={saveVisit}><Modal.Body>
        {monthlyDoctor && <React.Fragment>
          <p className="mb-2">Select a day, enter its location and time range, then save the visit.</p>
          <div className="doctor-editor-calendar" role="group" aria-label="Select OPD day">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label) => <div className="doctor-editor-weekday" key={label}>{label}</div>)}
            {Array.from({ length: (new Date(year, monthIndex, 1).getDay() + 6) % 7 }, (_, i) => <div key={`blank-${i}`} />)}
            {days.map((day) => {
              const date = dateFor(day);
              const appointments = doctorMonthVisits.filter((visit) => visit.date === date);
              return <button type="button" key={day} disabled={busy} className={`doctor-editor-day ${appointments.length ? "doctor-editor-day--booked" : ""}`} aria-label={`Select OPD day ${date}`} aria-pressed={form.date === date}
                onClick={() => chooseMonthlyVisit(appointments[0] || { doctorId: monthlyDoctor.id, date })}>
                <strong>{day}</strong>
                {appointments.length ? appointments.map((visit) => <small key={visit.id}>{visitTime(visit)}<span>{visit.nodeName} → {visit.customerName}</span></small>) : <small>No visit</small>}
              </button>;
            })}
          </div>
          <div className="doctor-editor-day-heading">
            <h5 className="mb-0">OPD · {form.date}</h5>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => chooseMonthlyVisit({ doctorId: monthlyDoctor.id, date: form.date })}>Add another visit on this day</button>
          </div>
          {dayVisits.length > 0 && <div className="doctor-editor-visits" aria-label="Visits on selected day">{dayVisits.map((visit) =>
            <button type="button" key={visit.id} disabled={busy} className={`btn btn-sm btn-${visit.id === form.id ? "primary" : "outline-primary"}`} onClick={() => chooseMonthlyVisit(visit)}>{visitTime(visit)} · {visit.nodeName} → {visit.customerName}</button>
          )}</div>}
        </React.Fragment>}
        {savedMessage && <div className="alert alert-success" role="status">{savedMessage}</div>}
        <fieldset disabled={busy} className={monthlyDoctor ? "doctor-editor-fields" : ""}>
          {!monthlyDoctor && <React.Fragment><div className="form-group"><label htmlFor="opd-doctor">Doctor</label><select id="opd-doctor" className="form-control" required value={form.doctorId} onChange={(event) => setForm({ ...form, doctorId: event.target.value })}><option value="">Select doctor</option>{doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.name}</option>)}</select></div>
          <div className="form-group"><label htmlFor="opd-date">OPD date</label><input id="opd-date" className="form-control" type="date" min="1000-01-01" required value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} /></div></React.Fragment>}
          <div className="form-group"><label htmlFor="opd-node">Node</label><select id="opd-node" className="form-control" required value={form.nodeId} onChange={(event) => setForm({ ...form, nodeId: event.target.value, customerId: "" })}><option value="">Select node</option>{nodes.map((node) => <option key={node.id} value={node.id}>{node.name}</option>)}</select></div>
          <div className="form-group"><label htmlFor="opd-customer">Customer</label><select id="opd-customer" className="form-control" required disabled={!form.nodeId || customerLoading || Boolean(customerError)} value={form.customerId} onChange={(event) => setForm({ ...form, customerId: event.target.value })}><option value="">{customerLoading ? "Loading customers…" : "Select customer"}</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></div>
          <div className="form-row">
            <div className="form-group col-sm-6"><label htmlFor="opd-start-time">Start time</label><input id="opd-start-time" className="form-control" type="time" required={!form.id || Boolean(form.endTime)} value={form.startTime} onChange={(event) => setForm({ ...form, startTime: event.target.value })} /></div>
            <div className="form-group col-sm-6"><label htmlFor="opd-end-time">End time</label><input id="opd-end-time" className="form-control" type="time" required={!form.id || Boolean(form.startTime)} value={form.endTime} onChange={(event) => setForm({ ...form, endTime: event.target.value })} /></div>
          </div>
          {form.nodeId && !customerLoading && !customerError && !customers.length && <p>No customers are configured for this node.</p>}
        </fieldset>
        {(formError || customerError) && <div className="alert alert-danger" role="alert">{formError || customerError}</div>}
        {confirmDelete && <div className="alert alert-warning">Delete this OPD visit for {form.doctorName} on {form.date}? <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={removeVisit}>Confirm delete</button></div>}
      </Modal.Body><Modal.Footer>
        {form.id && <button type="button" className="btn btn-outline-danger mr-auto" disabled={busy} onClick={() => setConfirmDelete(!confirmDelete)}>Delete</button>}
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={closeForm}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy || customerLoading || Boolean(customerError) || confirmDelete}>{busy ? "Saving…" : "Save"}</button>
      </Modal.Footer></form>}
    </Modal>
  </div></div>;
}
